import { SvgIconTypeMap, Tab, TabProps } from '@mui/material';
import Badge from '@mui/material/Badge';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import React from 'react';
import { Link } from 'react-router';

import { LeftBarNavigationTabStyles } from '@/components/ui/leftBarNavigation/leftBarNavigation.styles.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { AnnouncementAnchor } from '@/contexts/announcements/AnnouncementAnchor.comp.tsx';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { hasRouteAccess } from '@/models/route/routeAccess.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface Props extends TabProps {
  anchorKey?: string;
  badgeCount?: number;
  Icon: OverridableComponent<SvgIconTypeMap>;
  isRed?: boolean;
  selectedValue?: RouteEnum;
  value: RouteEnum;
}

export const LeftBarNavigationBar: React.FC<Props> = props => {
  const { anchorKey, ...rest } = props;

  if (anchorKey) {
    return (
      <AnnouncementAnchor anchorKey={anchorKey}>
        <LeftBarNavigationBarInner {...rest} />
      </AnnouncementAnchor>
    );
  }
  return <LeftBarNavigationBarInner {...rest} />;
};

const LeftBarNavigationBarInner: React.FC<Props> = props => {
  const { badgeCount, Icon, isRed, selectedValue, value, ...rest } = props;
  const { t } = useTranslationWithPrefix('leftNavigationBar');
  const { permissions } = useUserContext();

  if (!hasRouteAccess(value, permissions)) {
    return <span aria-hidden style={{ display: 'none' }} />;
  }

  const isEmergencyContacts = selectedValue && selectedValue === RouteEnum.EMERGENCY_CONTACTS;

  return (
    <Badge
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      badgeContent={badgeCount}
      color="warning"
      max={99}
      overlap="circular"
      sx={{
        '& .MuiBadge-badge': {
          right: 16,
          top: 12,
        },
        width: '100%',
      }}
    >
      <Tab
        component={Link}
        icon={
          <Icon
            sx={{
              fontSize: 24,
              ...(isRed && {
                color: themeColors.white,
              }),
              ...(isEmergencyContacts && {
                color: themeColors.red,
              }),
            }}
          />
        }
        label={t(value)}
        style={{
          ...(isRed && {
            backgroundColor: themeColors.red,
            color: themeColors.white,
            fontWeight: 'bold',
          }),
          ...(isEmergencyContacts && {
            backgroundColor: themeColors.white,
            color: themeColors.red,
            fontWeight: 'bold',
          }),
        }}
        sx={LeftBarNavigationTabStyles}
        to={value}
        value={value}
        viewTransition
        {...rest}
      />
    </Badge>
  );
};
